import { Component, OnInit } from '@angular/core'
import * as R from 'ramda'
import { MatDialogRef } from '@angular/material/dialog'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { ParsedUploadFile } from '@tv3/interfaces/files/parsed-upload.file'
import { asUTCEpoch, selectUserEntities, Toaster, User } from '@tokeet-frontend/tv3-platform'
import { AddTasks } from '@tokeet-frontend/tasks'
import { CsvFileColumnDef, CsvFileParserGuide } from '@tv3/interfaces/files/csv-file-parser.file'
import { generateMappingParser, parseDate, parseString } from '@tv3/shared/csv-file-parser/csv-data-parse-helper'
import * as moment from 'moment'
import { CreateTaskPayload, TaskStatus } from '@tokeet-frontend/tasks'

function validDateRange(item: any) {
  if (!moment(item.start).isBefore(item.due)) {
    return $localize`Start Date should be before Due Date.`
  }

  return true
}

@Component({
  selector: 'app-import-task-dialog',
  templateUrl: './import-task-dialog.component.html',
  host: { class: 'modal-content' },
  styleUrls: ['./import-task-dialog.component.scss'],
})
export class ImportTaskDialogComponent implements OnInit {
  files: ParsedUploadFile<CreateTaskPayload>[] = []

  columnDefs: CsvFileColumnDef[] = <CsvFileColumnDef[]>[
    { name: $localize`Name`, field: 'name', required: true, parse: parseString },
    { name: $localize`Start`, field: 'start', required: true, parse: parseDate },
    {
      name: $localize`Due`,
      field: 'due',
      required: true,
      parse: parseDate,

      rowValidators: [validDateRange],
    },
    {
      name: $localize`User Id`,
      field: 'user_id',
      required: true,
      parse: (str: string, col: CsvFileColumnDef) => {
        const { value, warning, error } = parseString(str, col)
        if (error) {
          return { error: error }
        }
        if (!this.usersById[value]) {
          return { error: $localize`User not found with this id` }
        }

        return { value, warning }
      },
    },
    { name: $localize`Inquiry Id`, field: 'inquiry_id', parse: parseString },
    { name: $localize`Notes`, field: 'notes', parse: parseString },
    {
      name: $localize`Status`,
      field: 'status',
      parse: generateMappingParser(
        {
          finished: TaskStatus.Completed,
          completed: TaskStatus.Completed,
          unfinished: TaskStatus.Pending,
          pending: TaskStatus.Pending,
          incompleted: TaskStatus.Pending,
          'due today': TaskStatus.Pending,
        },
        'pending'
      ),
      default: TaskStatus.Pending,
    },
  ]

  guide: CsvFileParserGuide = {
    description:
      $localize`The columns in your CSV file should be` +
      ` (${R.map((col) => (!col.required ? col.name : col.name + '*'), this.columnDefs).join(', ')}). ` +
      $localize`You can get the user ID/Inquiry ID on the details view`,
    list: [
      $localize`Name, Start, Due and User Id are required.`,
      $localize`Date format "YYYY-MM-DD", such as "2016-02-13".`,
    ],
    sample: [
      [
        $localize`Name`,
        $localize`Start`,
        $localize`Due`,
        $localize`User Id`,
        $localize`Inquiry Id`,
        $localize`Notes`,
        $localize`Status`,
      ],
      [
        $localize`Fix air condition`,
        '2025-02-13',
        '2025-02-14',
        'a3f19b90-64cb-4506-b269-cb9c7d0c254e',
        '',
        $localize`Note 1`,
        $localize`Completed`,
      ],
      [
        $localize`Clean the room`,
        '2025-02-12',
        '2025-02-14',
        'a3f19b90-64cb-4506-b269-cb9c7d0c254e',
        '',
        $localize`Note 2`,
        $localize`Pending`,
      ],
    ],
  }
  usersById: Record<string, User> = {}

  constructor(
    public dialogRef: MatDialogRef<ImportTaskDialogComponent>,
    private store: Store<fromRoot.State>,
    protected toast: Toaster
  ) {}

  ngOnInit(): void {
    this.store.pipe(select(selectUserEntities)).subscribe((users) => {
      this.usersById = users
    })
  }

  close() {
    this.dialogRef.close()
  }

  onCsvFilesParsed(file: ParsedUploadFile<any>) {
    this.files.push(file)
  }

  create() {
    if (R.any((file) => file.isError, this.files)) {
      this.toast.error($localize`There are some errors in selected file(s) needs to be checked.`)
      return
    }

    //  get parsed csv data
    let items: any[] = R.filter(
      (i) => !R.isEmpty(i),
      R.map((i) => i.item, R.flatten<any>(R.map((file) => file.items, this.files)))
    )
    items = R.map((item: any) => {
      item.due = asUTCEpoch(item.due)
      item.start = asUTCEpoch(item.start)
      item.user_ids = [item.user_id]
      delete item.user_id
      return item
    }, items)

    this.store.dispatch(AddTasks({ requests: items }))

    this.close()
  }
}
